import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { CheckCircle, Phone, Car } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to get proper image URL
const getImageUrl = (img) => {
  if (!img) return null;
  if (img.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${img}`;
  return img;
};

export default function VehiclesPage() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/vehicles`)
      .then(r => setVehicles(r.data.filter(v => v.is_available !== false)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="font-sans">
      <Navbar />
      <div className="pt-16">
        {/* Header */}
        <section className="py-20 bg-[#f8f5ef] text-center">
          <p className="text-xs font-semibold tracking-[0.3em] text-[#D4A843] uppercase mb-3">Notre véhicule</p>
          <h1 className="text-5xl font-bold text-gray-900 mb-4">Porsche Macan</h1>
          <p className="text-gray-500 max-w-xl mx-auto">
            Découvrez notre Porsche Macan avec chauffeur pour sublimer votre mariage.
            Elle est préparée et décorée avec le plus grand soin.
          </p>
        </section>

        {/* Vehicles grid */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            {loading ? (
              <div className="flex justify-center py-20">
                <div className="w-8 h-8 border-2 border-[#D4A843] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-10 max-w-3xl mx-auto">
                {vehicles.map(v => {
                  const mainImage = getImageUrl(v.images?.[0]);
                  const imageCount = v.images?.length || 0;
                  
                  return (
                    <article key={v.id} data-testid={`vehicle-${v.id}`} className="overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300">
                      <div className="relative overflow-hidden h-72 bg-gray-100">
                        {mainImage ? (
                          <img src={mainImage} alt={v.name}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-300">
                            <Car size={64} />
                          </div>
                        )}
                        {/* Image count badge */}
                        {imageCount > 1 && (
                          <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                            <Car size={12} /> {imageCount} photos
                          </div>
                        )}
                      </div>
                      <div className="p-8 bg-white">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="text-xs font-semibold tracking-widest text-[#D4A843] uppercase mb-1">{v.brand}</p>
                            <h2 className="text-2xl font-bold text-gray-900">{v.name}</h2>
                          </div>
                          <div className="text-right">
                            <span className="text-3xl font-bold text-gray-900">{v.price}€</span>
                            <p className="text-xs text-gray-400">par jour</p>
                          </div>
                        </div>
                        <p className="text-gray-500 text-sm leading-relaxed mb-5">{v.description}</p>
                        <div className="mb-6">
                          <p className="text-xs font-semibold tracking-widest text-gray-500 uppercase mb-3">Inclus dans la prestation</p>
                          <ul className="space-y-1.5">
                            {v.features?.map(f => (
                              <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                                <CheckCircle size={14} className="text-[#D4A843]" /> {f}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="flex gap-3">
                          <Link to={`/vehicules/${v.id}`} data-testid={`detail-btn-${v.id}`}
                            className="flex-1 text-center py-3 border-2 border-gray-900 text-gray-900 text-sm font-semibold tracking-wider hover:bg-gray-900 hover:text-white transition-all duration-200">
                            En savoir plus
                          </Link>
                          <Link to={`/reservation/${v.id}`} data-testid={`reserver-btn-${v.id}`}
                            className="flex-1 text-center py-3 bg-[#D4A843] text-white text-sm font-semibold tracking-wider hover:bg-[#c49933] transition-all duration-200">
                            Réserver
                          </Link>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}

            {/* Contact section */}
            <div className="mt-16 text-center py-12 bg-[#f8f5ef]">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Une prestation sur mesure ?</h3>
              <p className="text-gray-500 mb-6">Contactez-nous pour personnaliser la Porsche Macan selon votre mariage.</p>
              <a href="tel:0692042073" data-testid="contact-phone-btn"
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#1a1a1a] text-white text-sm font-semibold tracking-wider hover:bg-gray-800 transition-colors">
                <Phone size={16} /> Appelez-nous : 0692 04 20 73
              </a>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}
